import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import FlexRow from "../../../components/FlexRow";
import { TABLET_LANDSCAPE } from "../../../Constants";
import useAuth from "../../../hooks/useAuth";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import {
  getDate,
  getThisWeekFriday,
  getThisWeekMonday,
  getWeekNumOfMonth,
} from "../../../utils/date";
import CardLayout from "../CardLayout";
import Heading1 from "../Heading1";
import Heading2 from "../Heading2";
import NoDataText from "../components/NoDataText";
import FlexColumn from "../../../components/FlexColumn";

const Container = styled.div`
  margin-left: var(--sidebar-width);
  height: calc(100vh - 4.06rem);
  padding: 2.25rem 2.5rem;
  width: 100%;
  @media (max-width: ${TABLET_LANDSCAPE}) {
    margin-left: var(--sidebar-tablet-landscape-width);
  }
`;

const Section = styled.section`
  flex: 1;
`;

const SectionHeading = styled.h2`
  color: ${({ theme }) => theme.grayScaleColor.black};
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 140%;
`;

const Item = styled.div`
  padding: 1.25rem 2.25rem;
  border-radius: 0.75rem;
  border: 1px solid ${({ theme }) => theme.backgroundColor.darkgray10};
  background: ${({ theme }) => theme.backgroundColor.darkgray10};
  &:hover {
    background: ${({ theme }) => theme.primary.light};
    border: 1px solid ${({ theme }) => theme.primary.normal};
  }
  transition: all 0.3s;
  cursor: pointer;
`;

const ItemHeading1 = styled.span`
  line-height: 140%;
  color: ${({ theme }) => theme.grayScaleColor.black};
`;

const ItemHeading2 = styled.span`
  line-height: 140%;
  color: ${({ theme }) => theme.backgroundColor.darkgray40};
`;

const SLink = styled(Link)`
  text-decoration: none;
  color: inherit;
`;
const TextButton = styled.div`
  font-size: 0.8125rem;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 1.21875rem */
  letter-spacing: -0.03125rem;
  color: ${({ theme }) => theme.backgroundColor.darkgray40};
  padding: 0.38rem 0.75rem;
  border-radius: 0.4375rem;
  border: 1px solid ${({ theme }) => theme.grayScaleColor.gray30};
`;

const CalendarSvg = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="21"
      viewBox="0 0 20 21"
      fill="none"
    >
      <path
        d="M6.45641 12.2116C6.24702 12.2116 6.07181 12.1398 5.93078 11.9961C5.78975 11.8524 5.71924 11.6785 5.71924 11.4745C5.71924 11.2651 5.78975 11.0899 5.93078 10.9488C6.07181 10.8078 6.24568 10.7373 6.45241 10.7373C6.65914 10.7373 6.83436 10.8078 6.97805 10.9488C7.12175 11.0899 7.19359 11.2637 7.19359 11.4705C7.19359 11.6772 7.12175 11.8524 6.97805 11.9961C6.83436 12.1398 6.66048 12.2116 6.45641 12.2116ZM10.0478 12.2116C9.8437 12.2116 9.66983 12.1398 9.52614 11.9961C9.38243 11.8524 9.31057 11.6785 9.31057 11.4745C9.31057 11.2651 9.38243 11.0899 9.52614 10.9488C9.66983 10.8078 9.8437 10.7373 10.0478 10.7373C10.2518 10.7373 10.4257 10.8078 10.5694 10.9488C10.7131 11.0899 10.7849 11.2651 10.7849 11.4745C10.7849 11.6785 10.7131 11.8524 10.5694 11.9961C10.4257 12.1398 10.2518 12.2116 10.0478 12.2116ZM13.5333 12.2116C13.3293 12.2116 13.1554 12.1398 13.0117 11.9961C12.868 11.8524 12.7962 11.6785 12.7962 11.4745C12.7962 11.2651 12.868 11.0899 13.0117 10.9488C13.1554 10.8078 13.3293 10.7373 13.5333 10.7373C13.7374 10.7373 13.9113 10.8078 14.0549 10.9488C14.1986 11.0899 14.2705 11.2651 14.2705 11.4745C14.2705 11.6785 14.1986 11.8524 14.0549 11.9961C13.9113 12.1398 13.7374 12.2116 13.5333 12.2116ZM4.11991 18.4167C3.79311 18.4167 3.51109 18.2981 3.27384 18.0609C3.03659 17.8236 2.91797 17.5416 2.91797 17.2148V5.14758C2.91797 4.82078 3.03659 4.53876 3.27384 4.30151C3.51109 4.06426 3.79311 3.94564 4.11991 3.94564H5.65836V2.4873H6.70803V3.94564H13.3266V2.4873H14.3522V3.94564H15.8827C16.2094 3.94564 16.4915 4.06426 16.7287 4.30151C16.966 4.53876 17.0846 4.82078 17.0846 5.14758V17.2148C17.0846 17.5416 16.966 17.8236 16.7287 18.0609C16.4915 18.2981 16.2094 18.4167 15.8827 18.4167H4.11991ZM4.11991 17.4712H15.8827C15.9468 17.4712 16.0055 17.4445 16.059 17.3911C16.1124 17.3377 16.1391 17.2789 16.1391 17.2148V8.93762H3.86347V17.2148C3.86347 17.2789 3.89018 17.3377 3.94359 17.3911C3.99702 17.4445 4.0558 17.4712 4.11991 17.4712ZM3.86347 7.99214H16.1391V5.14758C16.1391 5.08346 16.1124 5.02469 16.059 4.97126C16.0055 4.91785 15.9468 4.89114 15.8827 4.89114H4.11991C4.0558 4.89114 3.99702 4.91785 3.94359 4.97126C3.89018 5.02469 3.86347 5.08346 3.86347 5.14758V7.99214Z"
        fill="#7C8585"
      />
    </svg>
  );
};

const PersonalCheckup = () => {
  const {
    auth: { user },
  } = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState([]);
  const [surveyYearMonth, setSurveyYearMonth] = useState([]);
  const [monthPeriod, setMonthPeriod] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const surveyEndAt = [new Date().getFullYear(), new Date().getMonth() + 1];
  const handleWeekReportClick = (e, id) => {
    navigate("/dashboard/checkup/report/week", { state: { surveyId: id } });
  };

  useEffect(() => {
    const fetch = async () => {
      const response = await axiosPrivate.get("/checkup-v2/surveys?type=me");
      if (!response.data.ok) {
        return;
      }
      if (response.data.surveys.length > 0) {
        setMonthPeriod(response.data.monthPeriod);
        const firstSurvey =
          response.data.surveys[response.data.surveys.length - 1];

        const [firstSurveyWeek, firstSurveyMonth] = getWeekNumOfMonth(
          new Date(firstSurvey.openAt)
        );
        const firstSurveyYear = new Date(firstSurvey.openAt).getFullYear();
        let y = firstSurveyYear;
        let m = firstSurveyMonth;
        const yearMonth = [];

        while (surveyEndAt[0] !== y || surveyEndAt[1] !== m) {
          // getWeekNumOfMonth(new Date(y, m-1,1));
          yearMonth.push([
            y,
            m,
            getDate(new Date(y, m - 1, 1), "MM.DD"),
            getDate(new Date(y, m, 0), "MM.DD"),
          ]);
          if (m > 12) {
            m = 1;
            y++;
          } else {
            m++;
          }
        }

        setSurveyYearMonth(yearMonth.reverse());
        setSurveys(response.data.surveys);
      }
      setIsLoading(false);
    };
    fetch();
  }, []);
  if (isLoading) {
    return null;
  }

  return (
    <Container>
      <Heading1>마음 체크업 결과</Heading1>
      <Heading2>주간/월간 마음 체크업 결과를 확인할 수 있어요</Heading2>
      {/* <LeaderReport /> */}

      <FlexRow style={{ marginTop: "3rem", gap: "2.87rem", height: "75%" }}>
        <Section>
          <FlexRow
            style={{
              justifyContent: "space-between",
              marginBottom: "1.5rem",
            }}
          >
            <SectionHeading>📊 주간 개인 마음 리포트</SectionHeading>
          </FlexRow>
          {surveys.length > 0 ? (
            <CardLayout
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                gap: "0.75rem",
                overflowY: "scroll",
              }}
            >
              {surveys.map((survey, index) => {
                const [week, month] = getWeekNumOfMonth(
                  new Date(survey.openAt)
                );
                const start = getThisWeekMonday(
                  new Date(survey.weekStartAt || survey.openAt),
                  "MM.DD"
                );
                const end = getThisWeekFriday(
                  new Date(survey.weekEndAt || survey.openAt),
                  "MM.DD"
                );
                const year = new Date(survey.openAt).getFullYear();
                return (
                  <Item
                    key={index}
                    onClick={(e) => handleWeekReportClick(e, survey._id)}
                  >
                    {/* <SLink
                    to={`/dashboard/checkup/report/week?surveyId=${survey._id}`}
                  > */}
                    <FlexRow style={{ justifyContent: "space-between" }}>
                      <FlexRow>
                        <ItemHeading2 style={{ marginRight: "2.75rem" }}>
                          {year}
                        </ItemHeading2>
                        <ItemHeading1>
                          {month}월 {week}주차 마음 체크업 결과
                        </ItemHeading1>
                      </FlexRow>
                      <FlexRow
                        style={{
                          width: "9rem",
                        }}
                      >
                        <CalendarSvg />
                        <ItemHeading2 style={{ marginLeft: "0.5rem" }}>
                          {`${start} ~ ${end}`}
                        </ItemHeading2>
                      </FlexRow>
                    </FlexRow>
                    {/* </SLink> */}
                  </Item>
                );
              })}
            </CardLayout>
          ) : (
            <CardLayout
              style={{
                height: "calc(100vh - 300px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <NoDataText />
            </CardLayout>
          )}
        </Section>
        <Section>
          <FlexRow
            style={{
              justifyContent: "space-between",
              marginBottom: "1.5rem",
            }}
          >
            <SectionHeading>📆 월간 개인 마음 리포트</SectionHeading>
            <SLink to={`/dashboard/checkup/report/month?type=all`}>
              <TextButton>전체 기간 리포트 보기</TextButton>
            </SLink>
          </FlexRow>
          {surveys.length > 0 ? (
            <CardLayout
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
                height: "100%",
                overflowY: "scroll",
              }}
            >
              {surveyYearMonth.map((item, index) => {
                if (!monthPeriod[item[1]]) {
                  return null;
                }
                return (
                  <Item key={index}>
                    <SLink
                      to={`/dashboard/checkup/report/month?year=${item[0]}&month=${item[1]}`}
                    >
                      <FlexRow style={{ justifyContent: "space-between" }}>
                        <FlexRow>
                          <ItemHeading2 style={{ marginRight: "2.75rem" }}>
                            {item[0]}
                          </ItemHeading2>
                          <ItemHeading1>
                            {item[1]}월 개인 마음 리포트
                          </ItemHeading1>
                        </FlexRow>
                        <FlexRow
                          style={{
                            width: "9rem",
                          }}
                        >
                          <CalendarSvg />
                          <ItemHeading2 style={{ marginLeft: "0.5rem" }}>
                            {`${getDate(
                              monthPeriod[item[1]][0] || item[2],
                              "MM.DD"
                            )} ~ ${getDate(
                              monthPeriod[item[1]][1] || item[3],
                              "MM.DD"
                            )}`}
                          </ItemHeading2>
                        </FlexRow>
                      </FlexRow>
                    </SLink>
                  </Item>
                );
              })}
            </CardLayout>
          ) : (
            <CardLayout
              style={{
                height: "calc(100vh - 300px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <NoDataText />
            </CardLayout>
          )}
        </Section>
      </FlexRow>

      {/* <ModalBackground>
        <div
          style={{
            width: "80%",
            height: "80%",
            background: "#EDF3F3",
            overflow: "scroll",
          }}
        >
          <LeaderReport />
        </div>
      </ModalBackground> */}
    </Container>
  );
};

export default PersonalCheckup;
